import { Box, Button, Skeleton, Table, Text } from "@mantine/core";
import { ArrowsClockwiseIcon, CaretDownIcon, CaretRightIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { Fragment, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import useSwr from "swr";
import { LedgerApi } from "../../apis";
import { IAccountsResponse, ILedgerAccount } from "../../apis/ledger";
import { Page, PageHeader } from "../../components/layout/Page";
import { ListToolbar, type ToolbarTab } from "../../components/list";
import sharedListClasses from "../../components/list/list.module.css";
import { currencyFormat } from "../../utils";
import classes from "./accounts.module.css";
import { ListStates, resolveListStates } from "../../components/list";

const COLUMN_COUNT = 4;
const SKELETON_ROWS = 8;
const TYPE_ORDER = ["ASSET", "LIABILITY", "EQUITY", "REVENUE", "EXPENSE"] as const;

const money = (v: number) => currencyFormat(Number(v || 0)).replace(/\s*₮$/, "");

type Row = { account: ILedgerAccount; depth: 0 | 1; balance: number; kids: number };
type Group = { type: string; rows: Row[]; total: number };

/**
 * Эцэг данс: `parentId` байвал тэр. Үгүй бол кодын цэгээс өмнөх хэсэг
 * («1200.BU-100182» → «1200») жагсаалтад байвал тэр. Хоёулаа олдохгүй бол
 * (1110.xxx, 1120.BARTER) дээд түвшинд, кодын дарааллаар, догол мөртэй гарна —
 * өөрийн үлдэгдлээрээ бүлгийн нийлбэрт орно.
 */
function parentOf(account: ILedgerAccount, byId: Map<string, ILedgerAccount>, byCode: Map<string, ILedgerAccount>) {
  if (account.parentId && byId.has(account.parentId)) return byId.get(account.parentId)!;
  const dot = account.code.indexOf(".");
  if (dot > 0) {
    const p = byCode.get(account.code.slice(0, dot));
    if (p && p.accountId !== account.accountId) return p;
  }
  return null;
}

/** Дансны төлөвлөгөө (§8.1) — зөвхөн харах; данс автоматаар үүснэ. */
export const LedgerAccountsPage = () => {
  const { t } = useTranslation(["billing", "common"]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get("tab") ?? "";
  const nonzero = searchParams.get("nonzero") === "1";
  const query = searchParams.get("q") ?? "";

  const patch = useCallback(
    (next: { tab?: string; nonzero?: boolean; q?: string }) => {
      const tab = next.tab ?? tabParam;
      const nz = next.nonzero ?? nonzero;
      const q = next.q ?? query;
      const p = new URLSearchParams();
      if (tab) p.set("tab", tab);
      if (nz) p.set("nonzero", "1");
      if (q) p.set("q", q);
      setSearchParams(p, { replace: true });
    },
    [tabParam, nonzero, query, setSearchParams],
  );

  const { data, error, isLoading, isValidating, mutate } = useSwr<IAccountsResponse>(
    "ledger.accounts",
    async () => await LedgerApi.getAccounts(),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  const accounts = useMemo(() => data?.accounts ?? [], [data]);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const needle = query.trim().toLowerCase();

  /** Бүх дансны мод (шүүлтүүргүй): хэвийн талын үлдэгдэл, нэгтгэх дансны нийлбэртэй. */
  const tree = useMemo(() => {
    const byId = new Map(accounts.map((a) => [a.accountId, a]));
    const byCode = new Map(accounts.map((a) => [a.code, a]));
    const kidsOf = new Map<string, ILedgerAccount[]>();
    const tops: ILedgerAccount[] = [];
    for (const a of accounts) {
      const p = parentOf(a, byId, byCode);
      if (p) {
        if (!kidsOf.has(p.accountId)) kidsOf.set(p.accountId, []);
        kidsOf.get(p.accountId)!.push(a);
      } else {
        tops.push(a);
      }
    }
    const byCodeAsc = (a: ILedgerAccount, b: ILedgerAccount) => a.code.localeCompare(b.code, "en", { numeric: true });
    tops.sort(byCodeAsc);
    return { tops, kidsOf, byCodeAsc };
  }, [accounts]);

  const typeCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of accounts) m.set(a.accountType, (m.get(a.accountType) ?? 0) + 1);
    return m;
  }, [accounts]);
  const types = useMemo(
    () => [
      ...TYPE_ORDER.filter((x) => typeCounts.has(x)),
      ...Array.from(typeCounts.keys()).filter((x) => !(TYPE_ORDER as readonly string[]).includes(x)),
    ],
    [typeCounts],
  );
  const tab = types.includes(tabParam) ? tabParam : "";

  const typeLabel = (x: string) => t(`billing:ledger.type.${x}`, { defaultValue: x });
  const scopeLabel = (x: string) => t(`billing:ledger.scope.${x}`, { defaultValue: x });

  const groups = useMemo<Group[]>(() => {
    const { tops, kidsOf, byCodeAsc } = tree;
    const matches = (a: ILedgerAccount, bal: number) =>
      (!needle || a.code.toLowerCase().includes(needle) || a.name.toLowerCase().includes(needle)) &&
      (!nonzero || bal !== 0);

    const out = new Map<string, Group>();
    for (const top of tops) {
      if (tab && top.accountType !== tab) continue;
      const kids = [...(kidsOf.get(top.accountId) ?? [])].sort(byCodeAsc);
      const rollup = top.balance + kids.reduce((s, k) => s + k.balance, 0);
      const visibleKids = kids.filter((k) => matches(k, k.balance));
      const selfShown = matches(top, rollup);
      if (!selfShown && visibleKids.length === 0) continue;

      const group = out.get(top.accountType) ?? { type: top.accountType, rows: [], total: 0 };
      out.set(top.accountType, group);
      // Эцэг нь жагсаалтад байхгүй дэд данс (1110.xxx, 1120.BARTER) дээд түвшинд, догол мөргүй.
      group.rows.push({ account: top, depth: 0, balance: rollup, kids: kids.length });
      group.total += rollup;
      if (!collapsed.has(top.accountId) || needle) {
        for (const k of visibleKids) group.rows.push({ account: k, depth: 1, balance: k.balance, kids: 0 });
      }
    }
    return types.filter((x) => out.has(x)).map((x) => out.get(x)!);
  }, [tree, tab, needle, nonzero, collapsed, types]);

  const shownCount = groups.reduce((s, g) => s + g.rows.length, 0);
  const hasFilters = !!(tab || nonzero || needle);
  const showError = !!error && !isValidating;
  const showSkeleton = isLoading || (isValidating && !data && !error);
  const ready = !!data && !showError;
  const dash = t("common:list.dash");

  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const openStatement = (a: ILedgerAccount) => {
    const now = dayjs();
    navigate(
      `/ledger/reports/account-statement?accountId=${encodeURIComponent(a.accountId)}` +
        `&dateFrom=${now.startOf("month").format("YYYY-MM-DD")}&dateTo=${now.endOf("month").format("YYYY-MM-DD")}`,
    );
  };

  const tabs: ToolbarTab[] = [
    { value: "", label: t("billing:ledger.accts.all"), count: ready ? accounts.length : undefined },
    ...types.map((x) => ({ value: x, label: typeLabel(x), count: ready ? typeCounts.get(x) : undefined })),
  ];

  const chipClass = `${sharedListClasses.filterChip}${nonzero ? ` ${sharedListClasses.filterChipActive}` : ""}`;

  const ar = data?.ar ?? null;
  const arOk = !!ar && ar.diff === 0;
  const signed = (v: number) => (v < 0 ? `−${money(Math.abs(v))}` : money(v));

  const renderBalance = (r: Row) => {
    const a = r.account;
    const zero = r.balance === 0;
    const abnormal = r.balance < 0;
    return (
      <Box component="span" className={classes.balanceInner}>
        {a.scope !== "CONTROL" && <Text span className={classes.hint}>{t("billing:ledger.accts.stmt")} →</Text>}
        <Text
          span
          className={`${classes.balance} ${zero ? classes.balanceZero : ""} ${abnormal ? classes.balanceAbnormal : ""}`}
          aria-label={`${a.code} ${zero ? "0" : signed(r.balance)}`}
        >
          {zero ? "-" : signed(r.balance)}
        </Text>
      </Box>
    );
  };

  const renderRow = (r: Row) => {
    const a = r.account;
    const sub = r.depth === 1;
    const expandable = r.kids > 0;
    const isOpen = !collapsed.has(a.accountId) || !!needle;
    // Нэгтгэх данс өөрөө хуулгагүй (бичилт дэд данс руу орно): дарахад зөвхөн нээх/хаах.
    const isControl = a.scope === "CONTROL";
    const activate = () => (isControl ? (expandable && toggle(a.accountId)) : openStatement(a));
    const scopeClass =
      a.scope === "CONTROL" ? classes.pillControl : a.scope === "SUBSIDIARY" ? classes.pillSub : classes.pillSystem;
    return (
      <Table.Tr
        key={a.accountId}
        className={classes.row}
        tabIndex={0}
        role={isControl ? undefined : "link"}
        aria-label={`${a.code} ${a.name}`}
        style={isControl && !expandable ? { cursor: "default" } : undefined}
        onClick={activate}
        onKeyDown={(ev) => {
          if (ev.target === ev.currentTarget && (ev.key === "Enter" || ev.key === " ")) {
            ev.preventDefault();
            activate();
          }
        }}
      >
        <Table.Td>
          <Box className={classes.codeCell}>
            {sub && <Box component="span" className={classes.indent} />}
            {expandable ? (
              <Box
                component="button"
                type="button"
                className={classes.toggle}
                aria-expanded={isOpen}
                aria-label={t(isOpen ? "billing:ledger.accts.collapse" : "billing:ledger.accts.expand")}
                onClick={(ev) => {
                  ev.stopPropagation();
                  toggle(a.accountId);
                }}
              >
                {isOpen ? <CaretDownIcon size={12} weight="bold" /> : <CaretRightIcon size={12} weight="bold" />}
              </Box>
            ) : (
              <Box component="span" className={classes.togglePad} />
            )}
            <Text span className={`${classes.code} ${sub ? classes.codeSub : ""}`}>{a.code}</Text>
          </Box>
        </Table.Td>
        <Table.Td>
          <Text span className={`${classes.name} ${sub ? classes.nameSub : ""}`}>{a.name}</Text>
        </Table.Td>
        <Table.Td>
          <Text span className={`${classes.pill} ${scopeClass}`}>{scopeLabel(a.scope)}</Text>
        </Table.Td>
        <Table.Td className={classes.balanceCell}>{renderBalance(r)}</Table.Td>
      </Table.Tr>
    );
  };

  const listStates = resolveListStates(
    <ListStates
      loading={showSkeleton}
      error={showError ? error : undefined}
      onRetry={() => mutate()}
      empty={showError || showSkeleton || shownCount === 0}
      filtered={hasFilters}
      onClear={() => setSearchParams(new URLSearchParams(), { replace: true })}
      columns={COLUMN_COUNT}
      emptyTitle={t("billing:ledger.accts.empty")}
    />
  );

  const renderBody = () => {
    return groups.map((g) => (
      <Fragment key={g.type}>
        <Table.Tr className={classes.groupRow}>
          <Table.Td colSpan={COLUMN_COUNT - 1}>{typeLabel(g.type)}</Table.Td>
          <Table.Td className={classes.groupTotal}>{g.total === 0 ? dash : signed(g.total)}</Table.Td>
        </Table.Tr>
        {g.rows.map(renderRow)}
      </Fragment>
    ));
  };

  return (
    <Page>
      <PageHeader
        title={
          <>
            {t("billing:ledger.accountsTitle")}
            {ready && <Text span className={classes.titleChip}>{accounts.length}</Text>}
          </>
        }
        subtitle={t("billing:ledger.accountsSubtitle")}
        extra={
          <Button variant="default" leftSection={<ArrowsClockwiseIcon size={14} />} onClick={() => mutate()}>
            {t("billing:ledger.accts.refresh")}
          </Button>
        }
      />

      <Box className={classes.kpi}>
        <Box className={classes.kpiCell}>
          <Text span className={classes.kpiLabel}>{t("billing:ledger.accts.kBal")}</Text>
          <Box className={classes.kpiValueRow}>
            <Text span className={classes.kpiValue}>{ready ? money(data!.totalDebit) : dash}</Text>
            {ready && (
              <Text span className={data!.balanced ? classes.badgeOk : classes.badgeBad}>
                {t(data!.balanced ? "billing:ledger.accts.kBalOk" : "billing:ledger.accts.kBalBad")}
              </Text>
            )}
          </Box>
          <Text span className={`${classes.kpiHint} ${ready && !data!.balanced ? classes.kpiHintBad : ""}`}>
            {ready
              ? data!.balanced
                ? " "
                : t("billing:ledger.accts.kDiff", { amount: money(Math.abs(data!.totalDebit - data!.totalCredit)) })
              : " "}
          </Text>
        </Box>

        <Box className={classes.kpiCell}>
          <Text span className={classes.kpiLabel}>{t("billing:ledger.accts.kCount")}</Text>
          <Text span className={classes.kpiValue}>{ready ? accounts.length : dash}</Text>
          <Box className={classes.typeCounts}>
            {ready &&
              types.map((x) => (
                <Text span key={x} size="xs">{typeLabel(x)} {typeCounts.get(x)}</Text>
              ))}
          </Box>
        </Box>

        <Box className={classes.kpiCell}>
          <Text span className={classes.kpiLabel}>{t("billing:ledger.accts.kAr")}</Text>
          <Box className={classes.kpiValueRow}>
            {ready && ar ? (
              arOk ? (
                <>
                  <Text span className={classes.kpiValue}>{money(0)}</Text>
                  <Text span className={classes.badgeOk}>{t("billing:ledger.accts.kArOk")}</Text>
                </>
              ) : (
                <Text span className={`${classes.kpiValue} ${classes.kpiValueBad}`}>{signed(ar.diff)}</Text>
              )
            ) : (
              <Text span className={classes.kpiValue}>{dash}</Text>
            )}
          </Box>
          <Text span className={classes.kpiHint}>
            {ready && ar ? (
              <>
                {t("billing:ledger.accts.kArFoot", { recv: money(ar.recvControl), ar: money(ar.arSubLedger) })}
                {!arOk && (
                  <>
                    {" · "}
                    <Box component="button" type="button" className={classes.link} onClick={() => navigate("/ledger/ar")}>
                      {t("billing:ledger.accts.kArLink")}
                    </Box>
                  </>
                )}
              </>
            ) : (
              " "
            )}
          </Text>
        </Box>
      </Box>

      <Box className={classes.card}>
        <ListToolbar
          tabs={tabs}
          activeTab={tab}
          onTabChange={(v) => patch({ tab: v })}
          search={{
            value: query,
            onChange: (v) => patch({ q: v }),
            placeholder: t("billing:ledger.accts.search"),
            w: 220,
          }}
          filterSlot={
            <Box component="button" type="button" className={chipClass} aria-pressed={nonzero} onClick={() => patch({ nonzero: !nonzero })}>
              <Text span size="sm" fw={nonzero ? 500 : undefined}>{t("billing:ledger.accts.nonzero")}</Text>
            </Box>
          }
        />

        <Box className={classes.wrap}>
          {listStates ?? (
          <Table className={classes.table} withRowBorders={false} horizontalSpacing={0} verticalSpacing={0}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("billing:ledger.colCode")}</Table.Th>
                <Table.Th>{t("billing:ledger.colName")}</Table.Th>
                <Table.Th>{t("billing:ledger.colScope")}</Table.Th>
                <Table.Th ta="right">{t("billing:ledger.colBalance")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{renderBody()}</Table.Tbody>
          </Table>
          )}
        </Box>
      </Box>
    </Page>
  );
};

export default LedgerAccountsPage;
